import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { notify } from 'reducers/NotifierReducer';
import {
  Button,
  makeStyles,
  withStyles,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Fab,
  LinearProgress,
  TableBody,
} from '@material-ui/core';
import SwapHorizontalCircleIcon from '@material-ui/icons/SwapHorizontalCircle';
import { handleError } from 'reducers/ErrorReducer';
import Tooltip from 'components/Tooltip';
import { genColHeaderName, sortObjArr } from 'utils/functions';
import { getService } from 'reducers/service';
import { displayDate, isValidDate } from 'reducers/TimeReducer';

const useStyles = makeStyles((theme) => ({
  table: {
    width: '100%',
    border: `1px solid ${theme.palette.colors.grey1}`,
    '& th': {
      padding: '2px 15px',
      '&:nth-child(1)': {
        width: '20%',
      },
      '&:nth-child(2)': {
        width: '30%',
      },
      '&:nth-child(3)': {
        width: '30%',
      },
    },
    '& td': {
      padding: '2px 15px',
      height: 33,
      '&:nth-child(1)': {
        width: '20%',
      },
      '&:nth-child(2)': {
        width: '30%',
      },
      '&:nth-child(3)': {
        width: '30%',
      },
    },
  },
  radioGroup: {
    flexDirection: 'row',
  },
  tableAction: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  loading: {
    paddingTop: 12,
    marginTop: -16,
    width: '100%',
    height: 16,
  },
}));

const RadioSmall = withStyles((theme) => ({
  root: {
    padding: 2,
    marginRight: 8,
    color: theme.palette.secondary.main,
    '&$checked': {
      color: theme.palette.secondary.main,
    },
  },
  checked: {},
}))((props) => <Radio color="default" {...props} />);

const dataColumns = [
  'ActiveWarrant',
  'AgeRangeIncrement',
  'Allergies',
  'BirthCitizenship',
  'BirthCountry',
  'BirthCounty',
  'BirthNationality',
  'BirthState',
  'BloodType',
  'Born',
  'CauseOfDeath',
  'ConcealedWeaponPermit',
  'CurrentCitizenship',
  'DNA',
  'Dangerous',
  'Deceased',
  'Died',
  'EducationLevel',
  'EnglishLangIndicator',
  'Ethnicity',
  'ExFelon',
  'EyeColor',
  'FPString',
  'FirstName',
  'Flags',
  'Gang',
  'GeneralAppearance',
  'HairColor',
  'HairLength',
  'HairStyle',
  'Handed',
  'HasDigitalImage',
  'HasDigitizedSignatureImage',
  'Height',
  'HenryFPString',
  'Incarcerated',
  'IsOrganDonor',
  'IsUSCitizen',
  'Juvenile',
  'LastName',
  'LocalResident',
  'MaritalStatus',
  'MiddleName',
  'Military',
  'NickName',
  'Notes',
  'Officer',
  'OverrideJuvenile',
  'Prefix',
  'PrimaryLanguage',
  'PrintsAvailable',
  'Probation',
  'ProtectiveOrder',
  'PublicOfficial',
  'Race',
  'SeniorCitizen',
  'Sex',
  'SexOffender',
  'Suffix',
  'Weight',
  'WhyDangerous',
  'ptsBirthCityID',
];

const getPersonDetails = async (ptsPersonID) => {
  if (!ptsPersonID) return null;
  const service = getService('ptsperson');
  const result = await service.find({
    query: { ptsPersonID, IsDeleted: false },
  });
  if (!result || !result.data || !result.data.length) return null;
  return result.data[0];
};

function valToStr(val) {
  if (isValidDate(val)) return displayDate(val);
  if (val === null) return 'null';
  if (typeof val === 'string' || typeof val === 'number') return '' + val;
  if (val === false) return 'false';
  if (val === true) return 'true';
  if (val === undefined) return 'Error, undefined value!';
  return 'unknown format!';
}

function ConfigureMerge(props) {
  const { source, setSource, target, setTarget, data, setData, loading, setLoading, rows } = props;

  const classes = useStyles();

  const [show, setShow] = useState('not-null'); // diff, not-null, all

  useEffect(() => {
    if ((!source || source.length === 0) && props.sourceID) {
      getData();
    }
  }, [props.sourceID, source]);

  const getData = async () => {
    setLoading(true);
    let target, source;
    try {
      source = rows.find((row) => row.ptsPersonID === props.sourceID);
      target = rows.find((row) => row.id === props.targetID);
    } catch (err) {
      props.handleError(err);
    }
    if (!target) return props.notify('Error setting target person', 'error');
    setSource([source]);
    setTarget(target);

    let data = processPersonData(source, target);
    data = sortObjArr(data, 'field');

    setData(data);
    setLoading(false);
  };

  const processPersonData = (source, target) => {
    const data = dataColumns.map((field) => {
      const sourceVal = source[field];
      const targetVal = target[field];
      const obj = {
        field,
        isTargetVal: !(sourceVal && !targetVal),
        headerName: genColHeaderName(field),
        sourceVal,
        targetVal,
        sourceStrVal: valToStr(sourceVal),
        targetStrVal: valToStr(targetVal),
      };

      return obj;
    });
    return data;
  };

  const swapSourceAndTarget = () => {
    const newSource = target ? [{ ...target }] : [];
    const newTarget = source?.length ? { ...source[0] } : null;
    const newData = swapData(data);
    setSource(newSource);
    setTarget(newTarget);
    setData(newData);
  };

  const swapData = (data) => {
    return data.map((row) => {
      const {
        field,
        isTargetVal,
        headerName,
        sourceVal,
        targetVal,
        sourceStrVal,
        targetStrVal,
      } = row;

      return {
        field,
        isTargetVal: !isTargetVal,
        headerName,
        sourceVal: targetVal,
        targetVal: sourceVal,
        sourceStrVal: targetStrVal,
        targetStrVal: sourceStrVal,
      };
    });
  };

  const onPersonClick = (row, isSource = true) => {
    const { field } = row;
    const idx = data.findIndex((row) => row.field === field);
    const newData = [...data];

    newData[idx].isTargetVal = isSource ? false : true;

    setData(newData);
  };

  const renderFilters = () => {
    return (
      <RadioGroup
        name="filter"
        value={show}
        onChange={(ev, val) => setShow(val)}
        className={classes.radioGroup}>
        <FormControlLabel value="all" control={<Radio />} label="All" />
        <FormControlLabel value="diff" control={<Radio />} label="Difference" />
        <FormControlLabel value="not-null" control={<Radio />} label="Not Null" />
      </RadioGroup>
    );
  };

  const selectAll = (isSource) => () => {
    const newData = [...data].map((row) => ({ ...row, isTargetVal: !isSource }));

    setData(newData);
  };

  const renderPersonRows = () => {
    let personData;

    switch (show) {
      case 'diff':
        personData = data.filter((row) => row.sourceVal !== row.targetVal);
        break;
      case 'not-null':
        personData = data.filter((row) => row.sourceVal !== null || row.targetVal !== null);
        break;
      default:
        personData = data;
    }

    return personData.map((row) => (
      <TableRow key={row.field}>
        <TableCell>{row.headerName}</TableCell>
        <TableCell>
          <RadioSmall
            checked={!row.isTargetVal}
            onClick={() => onPersonClick(row, true)}
            id={'source-' + row.field}
          />
          <label htmlFor={'source-' + row.field}>{row.sourceStrVal}</label>
        </TableCell>
        <TableCell>
          <RadioSmall
            checked={row.isTargetVal}
            onClick={() => onPersonClick(row, false)}
            id={'target-' + row.field}
          />
          <label htmlFor={'target-' + row.field}>{row.targetStrVal}</label>
        </TableCell>
      </TableRow>
    ));
  };

  const renderTable = () => {
    return (
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Person Record</TableCell>
            <TableCell>
              <div className={classes.tableAction}>
                <div>
                  Replace <br />
                  <small>(will be deleted)</small>
                </div>
                <div>
                  <Tooltip title="Merge Person">
                    <Fab
                      className="ml-2"
                      color="secondary"
                      size="small"
                      onClick={swapSourceAndTarget}
                      disabled={source.length > 0 ? source[0].IsDeleted : true}>
                      <SwapHorizontalCircleIcon />
                    </Fab>
                  </Tooltip>
                </div>
              </div>
            </TableCell>
            <TableCell>
              Keep <br />
              <small>(will be updated)</small>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Person ID</TableCell>
            <TableCell>{source.length > 0 ? source[0].PersonID : ''}</TableCell>
            <TableCell>{target.PersonID}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Bulk Selection</TableCell>
            <TableCell>
              <Button variant="outlined" onClick={selectAll(true)}>
                Select All Fields In This Section
              </Button>
            </TableCell>
            <TableCell>
              <Button variant="outlined" onClick={selectAll(false)}>
                Select All Fields In This Section
              </Button>
            </TableCell>
          </TableRow>
          {renderPersonRows()}
        </TableBody>
      </Table>
    );
  };

  return (
    <div>
      {renderFilters()}
      <div className={classes.loading}>{loading && <LinearProgress />}</div>
      {Boolean(source && target && !loading) && renderTable()}
    </div>
  );
}

export default connect(null, {
  handleError,
  notify,
})(ConfigureMerge);
